import { type DspRestriction, Feature_Flags_Enum } from '@openx/types/gqlTypes.ts';
import {
  type DspRestrictionModes,
  dspRestrictionModes,
  type OrganizationData,
  type OrganizationType,
  organizationType,
} from '@openx/types/organization.ts';
import { microToPercentWithPrecision } from '@openx/utils/lib/microToPercent.ts';
import { percentToMicroWithPrecision } from '@openx/utils/lib/percentToMicro.ts';
import omitBy from 'lodash/omitBy';
import { type AllowedCountries, organizationFields } from '../../constants.ts';
import type { OrganizationFormData } from '../../types.ts';
import type { CreateOrganizationPayload, EditOrganizationPayload } from '../../utils/context/OrganizationHookTypes.ts';

const transformDspRestrictionForPayload = (
  dspRestriction: DspRestriction[] | undefined,
  orgType?: OrganizationType | null
) => {
  if (
    !([organizationType.Buyer, organizationType.TechPartner] as OrganizationType[]).includes(
      orgType as OrganizationType
    )
  ) {
    return undefined;
  }

  const restrictionItems = dspRestriction?.filter(restrictionItem => restrictionItem.dsp_id !== null);

  if (!restrictionItems || restrictionItems.length === 0) {
    return undefined;
  }

  return restrictionItems.map(({ dsp_id, seat_ids }) => ({
    dsp_id,
    seat_ids: Array.isArray(seat_ids) ? seat_ids : [seat_ids],
  }));
};

const transformCreateFeatureFlagsForPayload = (
  formData: OrganizationFormData
): CreateOrganizationPayload['object']['feature_flags']['data'] => {
  const isTopLevelOrganization = formData.parent_account?.depth === 0;
  const featureFlagsMap: Partial<Record<Feature_Flags_Enum, boolean>> = {
    [Feature_Flags_Enum.CuratedDealBuyerDiscount]: !!formData[organizationFields.CURATED_DEAL_BUYER_DISCOUNTS_ENABLED],
    [Feature_Flags_Enum.FeeSplittingPartner]: !!formData[organizationFields.FEE_SPLITTING_PARTNER],
  };

  // top level organizations don't inherit feature flags
  if (isTopLevelOrganization) {
    return Object.entries(featureFlagsMap).map(([flag, isEnabled]) => ({
      value: flag as Feature_Flags_Enum,
      enabled: isEnabled,
    }));
  }

  // feature flags not sent in the payload are inherited from the parent organization,
  // so only flags whose value differs from the parent organization are sent
  const parentFeatureFlags = new Set(formData.parent_account?.feature_flags?.map(({ value }) => value));

  return Object.entries(featureFlagsMap)
    .filter(([flag, isEnabled]) => isEnabled !== parentFeatureFlags.has(flag as Feature_Flags_Enum))
    .map(([flag, isEnabled]) => ({ value: flag as Feature_Flags_Enum, enabled: isEnabled }));
};

export const transformCreateFormDataToApiPayload = (formData: OrganizationFormData): CreateOrganizationPayload => {
  const dspRestrictionPayload = transformDspRestrictionForPayload(formData.dsp_restriction, formData.organization_type);

  const data: CreateOrganizationPayload['object'] = {
    allowed_countries: formData.allowed_countries,
    allowed_domains: formData.allowed_domains,
    feature_flags: {
      data: transformCreateFeatureFlagsForPayload(formData),
    },
    description: formData.description,
    name: formData.name,
    organization_type: formData.organization_type || null,
    parent_account_id: formData.parent_account?.id || null,
    exchange_account_partner_id: formData.exchange_account_partner_id || null,
    default_cpm_cap: formData.default_cpm_cap?.toString() || null,
    default_fees_description: formData.default_fees_description || null,
    default_platform_share_ox_sourced: percentToMicroWithPrecision(formData.default_platform_share_ox_sourced),
    default_platform_share_partner_sourced: percentToMicroWithPrecision(
      formData.default_platform_share_partner_sourced
    ),
    default_share: percentToMicroWithPrecision(formData.default_share),
    max_cpm_cap: formData.max_cpm_cap?.toString() || null,
    max_share: percentToMicroWithPrecision(formData.max_share),
    dsp_restriction: dspRestrictionPayload,
    dsp_restriction_mode: dspRestrictionPayload ? formData.dsp_restriction_mode : undefined,
  };

  return {
    object: omitBy(data, value => value == null) as CreateOrganizationPayload['object'],
  };
};

export const isRevenueSharePartnerEnabled = (organization: OrganizationData): boolean => {
  return (
    !!organization.default_cpm_cap ||
    !!organization.default_fees_description ||
    !!organization.default_platform_share_ox_sourced ||
    !!organization.default_platform_share_partner_sourced ||
    !!organization.default_share ||
    !!organization.max_cpm_cap ||
    !!organization.max_share
  );
};

export const transformDataToMatchOrganizationForm = (organizationData: OrganizationData): OrganizationFormData => {
  const {
    allowed_countries,
    allowed_domains,
    description,
    feature_flags,
    name,
    parent_account,
    exchange_account_partner_id,
    max_share,
    max_cpm_cap,
    default_cpm_cap,
    default_fees_description,
    default_platform_share_ox_sourced,
    default_platform_share_partner_sourced,
    default_share,
    organization_type,
    dsp_restriction,
    dsp_restriction_mode,
    retargeting_enabled,
    path_to_root,
    sub_accounts,
  } = organizationData;

  const dspRestrictions =
    dsp_restriction?.map(({ dsp_id, seat_ids }) => ({
      dsp_id,
      seat_ids,
    })) || [];

  const dspRestrictionMode: DspRestrictionModes =
    dspRestrictions.length > 0 && !dsp_restriction_mode
      ? dspRestrictionModes.ALLOW
      : (dsp_restriction_mode as DspRestrictionModes);

  const isTopLevelOrganization = path_to_root !== undefined && path_to_root?.length <= 1;

  const shouldAllowToUpdateOrganizationType = sub_accounts?.length === 0 && isTopLevelOrganization;

  return {
    allowed_countries: (allowed_countries as AllowedCountries[]) || [],
    allowed_domains: allowed_domains || [],
    curated_deal_buyer_discounts_enabled: !!feature_flags.find(({ value }) => {
      return value === Feature_Flags_Enum.CuratedDealBuyerDiscount;
    }),
    fee_splitting_partner: !!feature_flags.find(({ value }) => {
      return value === Feature_Flags_Enum.FeeSplittingPartner;
    }),
    description: description || '',
    name,
    parent_account: {
      id: parent_account?.id || '',
      name: parent_account?.name || '',
    },
    organization_type: organization_type || organizationType.TechPartner,
    exchange_account_partner_id: exchange_account_partner_id || '',
    default_cpm_cap: default_cpm_cap || '',
    default_fees_description: default_fees_description ?? '',
    default_platform_share_ox_sourced: microToPercentWithPrecision(default_platform_share_ox_sourced) || '',
    default_platform_share_partner_sourced: microToPercentWithPrecision(default_platform_share_partner_sourced) || '',
    default_share: microToPercentWithPrecision(default_share) || '',
    max_cpm_cap: max_cpm_cap || '',
    max_share: microToPercentWithPrecision(max_share) || '',
    non_visible_deals: false,
    [organizationFields.IS_OPENX_REVENUE_SHARE_ENABLED]: isRevenueSharePartnerEnabled(organizationData),
    dsp_restriction: dspRestrictions,
    dsp_restriction_mode: dspRestrictionMode,
    retargeting_enabled: !!retargeting_enabled,
    [organizationFields.SHOULD_ALLOW_TO_UPDATE_ORGANIZATION_TYPE]: shouldAllowToUpdateOrganizationType,
    // Curated Deal Fees should be disabled for sub-organizations
    [organizationFields.SHOULD_DISABLE_CURATED_DEAL_FEES]: !isTopLevelOrganization,
  };
};

export const transformEditFormDataToApiPayload = (
  {
    allowed_countries,
    allowed_domains,
    curated_deal_buyer_discounts_enabled,
    description,
    fee_splitting_partner,
    name,
    exchange_account_partner_id,
    max_share,
    max_cpm_cap,
    default_cpm_cap,
    default_fees_description,
    default_platform_share_ox_sourced,
    default_platform_share_partner_sourced,
    default_share,
    organization_type,
    dsp_restriction,
    dsp_restriction_mode,
    retargeting_enabled,
  }: Partial<OrganizationFormData>,
  currentFeatureFlags: Feature_Flags_Enum[]
): EditOrganizationPayload['data'] => {
  const featureFlags = new Set(currentFeatureFlags);
  const dspRestrictionPayload = transformDspRestrictionForPayload(dsp_restriction, organization_type);

  const featureFlagsMap: Partial<Record<Feature_Flags_Enum, boolean | undefined>> = {
    [Feature_Flags_Enum.CuratedDealBuyerDiscount]: curated_deal_buyer_discounts_enabled,
    [Feature_Flags_Enum.FeeSplittingPartner]: fee_splitting_partner,
  };

  for (const [flag, isEnabled] of Object.entries(featureFlagsMap)) {
    if (isEnabled) {
      featureFlags.add(flag as Feature_Flags_Enum);
    } else {
      featureFlags.delete(flag as Feature_Flags_Enum);
    }
  }

  const data: EditOrganizationPayload['data'] = {
    allowed_countries,
    allowed_domains,
    description,
    name,
    exchange_account_partner_id: exchange_account_partner_id || null,
    feature_flags: [...featureFlags],
    default_cpm_cap: default_cpm_cap?.toString() || null,
    default_fees_description: default_fees_description || null,
    default_platform_share_ox_sourced: percentToMicroWithPrecision(default_platform_share_ox_sourced),
    default_platform_share_partner_sourced: percentToMicroWithPrecision(default_platform_share_partner_sourced),
    default_share: percentToMicroWithPrecision(default_share) || null,
    max_cpm_cap: max_cpm_cap?.toString() || null,
    max_share: percentToMicroWithPrecision(max_share),
    organization_type,
    dsp_restriction: dspRestrictionPayload || null,
    dsp_restriction_mode: dspRestrictionPayload ? dsp_restriction_mode : null,
    retargeting_enabled,
  };

  return omitBy(data, value => value === undefined);
};
